import React, { useState, useEffect } from 'react';
import { WarehouseNode, InventoryLevel, DemandData, DemandGap, Product, ContextData } from '../../types';
import { NodeService, InventoryService, ProductService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InventoryGrid from './InventoryGrid';
import DemandGrid from './DemandGrid';
import DemandGapDisplay from './DemandGapDisplay';
import './InventoryManagement.css';

const InventoryManagement: React.FC = () => {
  const { state: authState } = useAuth();
  const [nodes, setNodes] = useState<WarehouseNode[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [inventory, setInventory] = useState<InventoryLevel[]>([]);
  const [demand, setDemand] = useState<DemandData[]>([]);
  const [demandGaps, setDemandGaps] = useState<DemandGap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'inventory' | 'demand' | 'gaps'>('inventory');

  // Get tenant ID safely
  const tenantId = authState.user?.tenant_id;

  // Load data effect
  useEffect(() => {
    if (authState.isAuthenticated && tenantId) {
      loadData();
    }
  }, [authState.isAuthenticated, tenantId]);

  // Context data effect for AI Assistant
  useEffect(() => {
    if (!loading && nodes.length > 0 && authState.isAuthenticated && tenantId) {
      const contextData = extractContextData();
      // Store context data in window object for AI Assistant access
      (window as any).warehouseContextData = {
        ...contextData,
        tenant_context: {
          tenant_id: tenantId,
          user_id: authState.user?.user_id,
          user_role: authState.user?.role
        }
      };
    }
  }, [nodes, products, inventory, demand, demandGaps, activeTab, loading, tenantId, authState]);

  // Ensure user is authenticated and has tenant context
  if (!authState.isAuthenticated || !tenantId) {
    return (
      <div className="inventory-management">
        <div className="error">
          <h3>Authentication Required</h3>
          <p>Please log in to access inventory management.</p>
        </div>
      </div>
    );
  }

  // Context extraction function for AI Assistant
  const extractContextData = (): ContextData => {
    // Calculate summary metrics
    const totalInventory = inventory.reduce((sum, item) => sum + item.quantity, 0);
    const totalDemand = demand.reduce((sum, item) => sum + item.quantity, 0);
    
    const shortages = demandGaps.filter(gap => gap.gap > 0);
    const surpluses = demandGaps.filter(gap => gap.gap < 0);
    
    const totalShortage = shortages.reduce((sum, gap) => sum + gap.gap, 0);
    const totalSurplus = Math.abs(surpluses.reduce((sum, gap) => sum + gap.gap, 0));
    
    const nodesWithShortage = new Set(shortages.map(gap => gap.node_id)).size;
    const productsWithShortage = new Set(shortages.map(gap => gap.product_id)).size;

    return {
      filters: {
        active_tab: activeTab,
        tenant_id: tenantId, // Include tenant context
        // Add selected filters if implemented
      },
      nodes,
      products,
      inventory,
      demand,
      demand_gaps: demandGaps,
      summary: {
        total_inventory: totalInventory,
        total_demand: totalDemand,
        total_shortage: totalShortage,
        total_surplus: totalSurplus,
        nodes_with_shortage: nodesWithShortage,
        products_with_shortage: productsWithShortage,
      }
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all required data
      const [nodesData, productsData, inventoryData, demandData] = await Promise.all([
        NodeService.getNodes(),
        ProductService.getProducts(),
        InventoryService.getInventory(),
        InventoryService.getDemand()
      ]);

      setNodes(nodesData);
      setProducts(productsData);
      setInventory(inventoryData);
      setDemand(demandData);

      // Calculate demand gaps
      await loadDemandGaps();
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadDemandGaps = async () => {
    try {
      const gaps = await InventoryService.getDemandGaps();
      setDemandGaps(gaps);
    } catch (err: any) {
      console.error('Error loading demand gaps:', err);
      // Calculate gaps locally if API fails
      calculateDemandGapsLocally();
    }
  };

  const calculateDemandGapsLocally = () => {
    const gaps: DemandGap[] = [];
    
    nodes.forEach(node => {
      products.forEach(product => {
        const inventoryItem = inventory.find(
          inv => inv.node_id === node.node_id && inv.product_id === product.product_id
        );
        const demandItem = demand.find(
          dem => dem.node_id === node.node_id && dem.product_id === product.product_id
        );

        const inventoryQty = inventoryItem?.quantity || 0;
        const demandQty = demandItem?.quantity || 0;
        const gap = demandQty - inventoryQty;

        gaps.push({
          node_id: node.node_id,
          product_id: product.product_id,
          inventory_quantity: inventoryQty,
          demand_quantity: demandQty,
          gap: gap
        });
      });
    });

    setDemandGaps(gaps);
  };

  const handleInventoryUpdate = async (nodeId: string, productId: string, quantity: number) => {
    try {
      const updatedInventory = await InventoryService.updateInventory({
        node_id: nodeId,
        product_id: productId,
        quantity: quantity
      });

      // Update local state
      setInventory(prev => {
        const existing = prev.find(inv => inv.node_id === nodeId && inv.product_id === productId);
        if (existing) {
          return prev.map(inv => 
            inv.node_id === nodeId && inv.product_id === productId 
              ? updatedInventory 
              : inv
          );
        } else {
          return [...prev, updatedInventory];
        }
      });

      // Recalculate demand gaps
      await loadDemandGaps();
    } catch (err: any) {
      setError(err.message || 'Failed to update inventory');
    }
  };

  const handleDemandUpdate = async (nodeId: string, productId: string, quantity: number) => {
    try {
      const updatedDemand = await InventoryService.updateDemand({
        node_id: nodeId,
        product_id: productId,
        quantity: quantity,
        forecast_period: 'current' // Default forecast period
      });

      // Update local state
      setDemand(prev => {
        const existing = prev.find(dem => dem.node_id === nodeId && dem.product_id === productId);
        if (existing) {
          return prev.map(dem => 
            dem.node_id === nodeId && dem.product_id === productId 
              ? updatedDemand 
              : dem
          );
        } else {
          return [...prev, updatedDemand];
        }
      });

      // Recalculate demand gaps
      await loadDemandGaps();
    } catch (err: any) {
      setError(err.message || 'Failed to update demand');
    }
  };

  if (loading) {
    return (
      <div className="inventory-management">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading inventory and demand data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inventory-management">
        <div className="error">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="inventory-management">
        <div className="empty-state">
          <h3>No Warehouse Nodes Found</h3>
          <p>You need to create warehouse nodes before managing inventory and demand.</p>
          <p>Please go to the Nodes section to add warehouse locations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-management">
      <div className="header">
        <h2>Inventory & Demand Management</h2>
        <p>Manage inventory levels and customer demand across your warehouse network</p>
        {authState.user?.tenant?.name && (
          <div className="tenant-context">
            <span className="tenant-label">Organization:</span>
            <span className="tenant-name">{authState.user.tenant.name}</span>
          </div>
        )}
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          Inventory Levels
        </button>
        <button 
          className={`tab ${activeTab === 'demand' ? 'active' : ''}`}
          onClick={() => setActiveTab('demand')}
        >
          Customer Demand
        </button>
        <button 
          className={`tab ${activeTab === 'gaps' ? 'active' : ''}`}
          onClick={() => setActiveTab('gaps')}
        >
          Demand Gaps
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'inventory' && (
          <InventoryGrid
            nodes={nodes}
            products={products}
            inventory={inventory}
            onInventoryUpdate={handleInventoryUpdate}
          />
        )}
        
        {activeTab === 'demand' && (
          <DemandGrid
            nodes={nodes}
            products={products}
            demand={demand}
            onDemandUpdate={handleDemandUpdate}
          />
        )}
        
        {activeTab === 'gaps' && (
          <DemandGapDisplay
            nodes={nodes}
            products={products}
            demandGaps={demandGaps}
            onRefresh={loadDemandGaps}
          />
        )}
      </div>

      <div className="actions">
        <button className="btn btn-secondary" onClick={loadData}>
          Refresh Data
        </button>
      </div>
    </div>
  );
};

export default InventoryManagement;